// AdminConfig — admin sub-view, extracted verbatim from App.js.
import { useState } from "react";
import { C } from "../lib/theme";
import { fmt } from "../lib/format";
import { Card, SectionHdr } from "../components/ui";
import { ChannelSettings } from "../components/ChannelSettings";
import { calcItem } from "../lib/calc";
import { saveFixedCostsToDB, saveWorkspaceRatesToDB, setVatOnFees } from "../data/db";
import { useT } from "../lib/i18n";
import { isOverrideActive } from "../lib/accountAccess";

export function AdminConfig({cfg, setCfg, tiers, setTiers, workspaces, setWorkspaces, wsId, chId, account, onShowUpgrade}) {
  const { t } = useT();
  const [saved,setSaved]=useState(false);
  const [editTier,setEditTier]=useState(null);
  const [newTier,setNewTier]=useState({label:"",discount:0,fee:0});
  const [savingFc,setSavingFc]=useState(false);
  const [fcSaved,setFcSaved]=useState(false);
  const [savingRates,setSavingRates]=useState(false);
  const [ratesSaved,setRatesSaved]=useState(false);
  const ws = workspaces?.[wsId] || (workspaces ? Object.values(workspaces)[0] : null);
  const fc = ws?.fixedCosts || {rent:0,wages:0,utilities:0,other:0};

  // Workspace VAT/COGS — fall back to market default
  const wsVatRate = ws?.vatRate ?? ((ws?.market === "uae" || ws?.market === "in") ? 0.05 : 0.20);
  const wsCogsPct = ws?.cogsPct ?? 0;

  function saveRates(){setSaved(true);setTimeout(()=>setSaved(false),2000);}

  async function saveFixedCosts(newCosts){
    if(!ws?.id) return;
    setSavingFc(true);
    await saveFixedCostsToDB(ws.id, newCosts);
    if(setWorkspaces) setWorkspaces(prev=>({...prev,[ws.id]:{...prev[ws.id],fixedCosts:newCosts}}));
    setFcSaved(true);
    setTimeout(()=>setFcSaved(false),2000);
    setSavingFc(false);
  }

  function updateFc(field, val){
    const newCosts = {...fc, [field]: parseFloat(val)||0};
    saveFixedCosts(newCosts);
  }

  async function saveWsRate(field, percentVal){
    if(!ws?.id) return;
    setSavingRates(true);
    const newRate = (parseFloat(percentVal)||0)/100;
    const updates = field === "vat" ? {vatRate:newRate, cogsPct:wsCogsPct} : {vatRate:wsVatRate, cogsPct:newRate};
    await saveWorkspaceRatesToDB(ws.id, updates);
    if(setWorkspaces) setWorkspaces(prev=>({...prev,[ws.id]:{...prev[ws.id],...updates}}));
    setRatesSaved(true);
    setTimeout(()=>setRatesSaved(false),2000);
    setSavingRates(false);
  }

  async function saveVatOnFees(newValue){
    if(!ws?.id) return;
    setSavingRates(true);
    // Optimistic update — flip the UI immediately
    if(setWorkspaces) setWorkspaces(prev=>({...prev,[ws.id]:{...prev[ws.id], vatOnFees: newValue}}));
    // Try to save to DB
    const { error } = await setVatOnFees(ws.id, newValue);
    if (error) {
      // Roll back the optimistic update
      if(setWorkspaces) setWorkspaces(prev=>({...prev,[ws.id]:{...prev[ws.id], vatOnFees: !newValue}}));
      // Show clear error to user
      const isMissingColumn = error.message?.toLowerCase().includes("column") || error.message?.toLowerCase().includes("vat_on_fees");
      alert(isMissingColumn
        ? "Cannot save: the 'vat_on_fees' column hasn't been added to your database yet. Run this in Supabase SQL Editor:\n\nALTER TABLE workspaces\nADD COLUMN IF NOT EXISTS vat_on_fees BOOLEAN NOT NULL DEFAULT TRUE;"
        : "Save failed: " + error.message);
      console.error("saveVatOnFees error:", error);
      setSavingRates(false);
      return;
    }
    setRatesSaved(true);
    setTimeout(()=>setRatesSaved(false),2000);
    setSavingRates(false);
  }
  const wsVatOnFees = ws?.vatOnFees === true;

  const totalFixed = (fc.rent||0)+(fc.wages||0)+(fc.utilities||0)+(fc.other||0);
  const ex=calcItem(5,50,0.50,0,{...cfg,vatRate:wsVatRate,cogsPct:wsCogsPct});

  // Trial detection — show a hint that they only see 1 aggregator until they subscribe
  const isTrialing = account?.subscription_status === "trialing";
  const isFreeOverride = isOverrideActive(account);
  const showTrialHint = isTrialing && !isFreeOverride;
  const channelCount = ws?.channels ? Object.keys(ws.channels).length : 0;

  return (
    <div style={{padding:24}}>
      <SectionHdr title={t("config.heading")} sub={`${t("config.sub.prefix")}${ws?.name||t("config.sub.thisWorkspace")}${t("config.sub.suffix")}`}/>

      {showTrialHint && (
        <div style={{
          marginBottom:18,
          padding:"14px 18px",
          background:"#fff",
          borderRadius:10,
          border:"1px solid #e2e8f0",
          boxShadow:"0 1px 2px rgba(15,23,42,.04)",
          display:"flex",alignItems:"center",justifyContent:"space-between",gap:14,flexWrap:"wrap",
        }}>
          <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
            <span style={{
              fontSize:10,fontWeight:700,letterSpacing:.6,textTransform:"uppercase",
              color:"#475569",background:"#f1f5f9",padding:"3px 8px",borderRadius:4,flexShrink:0,
            }}>
              {t("config.trial.badge")}
            </span>
            <div style={{minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,color:"#0f172a",letterSpacing:"-.1px"}}>
                {t("config.trial.configuringPrefix")}{channelCount === 1 ? t("config.trial.oneAggregator") : `${channelCount}${t("config.trial.nAggregatorsSuffix")}`}
              </div>
              <div style={{fontSize:11.5,color:"#64748b",fontWeight:500,marginTop:2,letterSpacing:"-.05px"}}>
                {t("config.trial.note")}
              </div>
            </div>
          </div>
          {onShowUpgrade && (
            <button onClick={onShowUpgrade} style={{
              padding:"7px 14px",borderRadius:7,border:"1px solid #e2e8f0",
              background:"#fff",color:"#0f172a",cursor:"pointer",
              fontSize:12,fontWeight:600,fontFamily:"inherit",letterSpacing:"-.1px",
              flexShrink:0,
            }}>
              {t("config.trial.upgrade")}
            </button>
          )}
        </div>
      )}

      <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,alignItems:"start"}}>
        {/* LEFT COLUMN: Workspace-level (Fixed costs + Global rates) */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {/* Fixed costs — per workspace */}
          {ws && (
            <Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:C.text}}>{t("config.fixed.titlePrefix")}{ws.name}</div>
                  <div style={{fontSize:12,color:C.muted,marginTop:3}}>{t("config.fixed.desc")}</div>
                </div>
                {fcSaved && <span style={{fontSize:11,color:C.green,fontWeight:600}}>{t("config.saved")}</span>}
                {savingFc && <span style={{fontSize:11,color:C.muted}}>{t("config.saving")}</span>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                {[
                  ["rent",t("config.fixed.rent")],
                  ["wages",t("config.fixed.wages")],
                  ["utilities",t("config.fixed.utilities")],
                  ["other",t("config.fixed.other")],
                ].map(([key,label])=>(
                  <div key={key}>
                    <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{label}</label>
                    <input
                      key={`${key}-${ws?.id}`}
                      defaultValue={fc[key]||""}
                      onBlur={e=>updateFc(key, e.target.value)}
                      type="number" placeholder="0"
                      style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
                  </div>
                ))}
              </div>
              <div style={{padding:"10px 14px",background:C.blue10,border:`1px solid ${C.blue}33`,borderRadius:7,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:13,color:C.text,fontWeight:500}}>{t("config.fixed.totalLabel")}</span>
                <span style={{fontSize:15,fontWeight:700,color:C.blue}}>{fmt(totalFixed)} <span style={{fontSize:11,fontWeight:400,color:C.muted}}>{t("config.fixed.perMonth")}</span></span>
              </div>
              <div style={{fontSize:11,color:C.muted2,marginTop:8}}>{t("config.fixed.tip")}</div>
            </Card>
          )}

          {/* Workspace VAT + COGS rates */}
          <Card>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:C.text,letterSpacing:"-.2px"}}>{t("config.rates.titlePrefix")}{ws?.name||t("config.sub.thisWorkspace")}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:4,fontWeight:500}}>{t("config.rates.desc")}</div>
              </div>
              {ratesSaved && <span style={{fontSize:11.5,color:C.greenDark,fontWeight:700,padding:"4px 10px",borderRadius:14,background:C.green10,border:`1px solid ${C.green}33`}}>{t("config.saved")}</span>}
              {savingRates && <span style={{fontSize:11.5,color:C.muted,fontWeight:600}}>{t("config.saving")}</span>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:18}}>
              <div>
                <label style={{fontSize:10.5,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:6}}>{t("config.rates.vatLabel")}</label>
                <input
                  key={`vat-${ws?.id}`}
                  defaultValue={(wsVatRate*100).toFixed(1)}
                  onBlur={e=>saveWsRate("vat", e.target.value)}
                  type="number" step="0.1"
                  className="num"
                  style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13.5,fontFamily:"inherit",boxSizing:"border-box",background:"#fff",fontWeight:600,color:C.text}}/>
                <div style={{fontSize:11,color:C.muted2,marginTop:5,fontWeight:500}}>{ws?.market==="uae"?t("config.rates.vatHintUae"):ws?.market==="in"?t("config.rates.vatHintIn"):t("config.rates.vatHintUk")}</div>
              </div>
              <div>
                <label style={{fontSize:10.5,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:6}}>{t("config.rates.cogsLabel")}</label>
                <input
                  key={`cogs-${ws?.id}`}
                  defaultValue={(wsCogsPct*100).toFixed(1)}
                  onBlur={e=>saveWsRate("cogs", e.target.value)}
                  type="number" step="0.1"
                  className="num"
                  style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13.5,fontFamily:"inherit",boxSizing:"border-box",background:"#fff",fontWeight:600,color:C.text}}/>
                <div style={{fontSize:11,color:C.muted2,marginTop:5,fontWeight:500}}>{t("config.rates.cogsHint")}</div>
              </div>
            </div>

            {/* VAT on aggregator fees — always applied (no toggle). #25 */}
            <div style={{padding:14,background:C.bg3,borderRadius:8,border:`1px solid ${C.border2}`,marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:3}}>{t("config.rates.vatOnFeesTitle")}</div>
              <div style={{fontSize:11.5,color:C.muted,lineHeight:1.5}}>
                {(wsVatRate*100).toFixed(0)}{t("config.rates.vatOnFeesDescPrefix")}
              </div>
            </div>
            <div style={{fontSize:11,color:C.muted2,marginTop:4,fontWeight:500}}>{t("config.rates.footnote")}</div>
          </Card>
        </div>

        {/* RIGHT COLUMN: Per-platform (commission, card fee) */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {ws && (() => {
            const allChannels = Object.values(ws.channels||{});
            if(allChannels.length===0) return (
              <Card>
                <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:6}}>{t("config.platform.title")}</div>
                <div style={{fontSize:12,color:C.muted}}>{t("config.platform.emptyDesc")}</div>
              </Card>
            );
            const selectedCh = chId && ws.channels?.[chId] ? ws.channels[chId] : allChannels[0];
            return (
              <Card>
                <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:3}}>{t("config.platform.title")}</div>
                <div style={{fontSize:12,color:C.muted,marginBottom:14}}>{t("config.platform.desc")}</div>
                <ChannelSettings channel={selectedCh} setWorkspaces={setWorkspaces} wsId={wsId}/>
              </Card>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
